import { useApolloClient } from '@apollo/client';
import React, { useCallback } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { useCompleteFragment } from '@eluve/apollo-client';
import { Switch, useToast } from '@eluve/components';
import { FeatureFlagsEnum } from '@eluve/graphql-types';
import {
  useUserDisplayFromSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { adminTenantFeatureFlagFragment } from './gql-operations';

export interface TenantFeatureFlagToggleProps {
  tenantId: string;
  flag: FeatureFlagsEnum;
}

export const TenantFeatureFlagToggle: React.FC<
  TenantFeatureFlagToggleProps
> = ({ tenantId, flag }) => {
  const toast = useToast();
  const apiClient = useApiClient();
  const apolloClient = useApolloClient();
  const userId = useUserIdFromSession();
  const { email, firstName, lastName } = useUserDisplayFromSession();

  const data = useCompleteFragment({
    fragment: adminTenantFeatureFlagFragment,
    key: {
      flag,
      tenantId,
    },
    strict: false,
  });

  const submit = useCallback(
    async (isEnabled: boolean) => {
      const revertCache = () => {
        apolloClient.cache.writeFragment({
          fragment: adminTenantFeatureFlagFragment,
          data: {
            __typename: 'TenantFeatureFlags' as const,
            flag,
            tenantId,
            isEnabled: !isEnabled,
            updatedAt: data?.updatedAt ?? new Date().toISOString(),
            updatedById: data?.updatedById ?? null,
            updatedByUser: data?.updatedByUser ?? null,
            featureFlag: {
              __typename: 'FeatureFlags' as const,
              name: flag,
              description: data?.featureFlag?.description ?? null,
            },
          },
        });
        toast.error(`Failed to update access to feature ${flag}`);
      };

      // Optimistic cache update
      apolloClient.cache.writeFragment({
        fragment: adminTenantFeatureFlagFragment,
        data: {
          __typename: 'TenantFeatureFlags' as const,
          flag,
          tenantId,
          isEnabled,
          updatedAt: new Date().toISOString(),
          updatedById: userId,
          updatedByUser: {
            __typename: 'Users' as const,
            id: userId,
            firstName: firstName ?? null,
            lastName: lastName ?? null,
            email,
          },
          featureFlag: {
            __typename: 'FeatureFlags' as const,
            name: flag,
            description: data?.featureFlag?.description ?? null,
          },
        },
      });

      try {
        const response = await apiClient.adminFeatureFlags.setTenantFeatureFlag(
          {
            body: { tenantId, flag, isEnabled },
          },
        );

        if (response.status !== 200) {
          revertCache();
        }
      } catch {
        revertCache();
      }
    },
    [
      apolloClient,
      apiClient,
      tenantId,
      flag,
      data,
      toast,
      userId,
      email,
      firstName,
      lastName,
    ],
  );

  return (
    <Switch
      size="sm"
      onCheckedChange={(isChecked) => submit(isChecked)}
      checked={data?.isEnabled ?? false}
      aria-label={`Toggle ${flag}`}
    />
  );
};
